import { Link } from 'react-router-dom';
import { useTranslation } from '../lib/i18n';
import { Shield, ArrowLeft } from 'lucide-react';

export default function Terms() {
  const { language } = useTranslation();

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      {/* Header */}
      <header className="border-b border-white/5 bg-[#050505]/90 backdrop-blur-lg sticky top-0 z-40">
        <div className="max-w-5xl mx-auto px-6 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Shield className="w-5 h-5 text-blue-600" strokeWidth={1.5} />
            <span className="font-heading text-lg tracking-tight">SheetArmor</span>
          </div>
          <Link to="/" className="flex items-center gap-2 text-neutral-400 hover:text-white text-sm font-body transition-colors">
            <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
            <span>Back</span>
          </Link>
        </div>
      </header>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-12">
        <h1 className="font-heading text-4xl text-white mb-2">
          {language === 'fr' ? 'Conditions Générales d\'Utilisation' : 'Terms of Service'}
        </h1>
        <p className="text-neutral-500 text-sm font-body mb-10">
          {language === 'fr' ? 'Dernière mise à jour : 25 février 2026' : 'Last updated: February 25, 2026'}
        </p>

        {language === 'fr' ? (
          <div className="space-y-8 text-neutral-300 font-body leading-relaxed">
            <section>
              <h2 className="text-xl text-white font-heading mb-3">1. Acceptation des Conditions</h2>
              <p>
                En accédant et en utilisant SheetArmor (ci-après "le Service"), vous acceptez d'être lié par les présentes Conditions Générales d'Utilisation.
                Si vous n'acceptez pas ces conditions, veuillez ne pas utiliser le Service.
              </p>
            </section>

            <section>
              <h2 className="text-xl text-white font-heading mb-3">2. Description du Service</h2>
              <p>
                SheetArmor est une plateforme SaaS B2B permettant l'audit automatisé de fichiers Excel (.xlsx) via une analyse déterministe
                combinée à un moteur d'intelligence artificielle. Le Service génère des rapports PDF professionnels identifiant les risques potentiels
                dans les modèles financiers.
              </p>
            </section>

            <section>
              <h2 className="text-xl text-white font-heading mb-3">3. Conditions d'Accès</h2>
              <p className="mb-2">
                Le Service est exclusivement réservé aux entreprises et organisations professionnelles. L'inscription requiert :
              </p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li>Une adresse email professionnelle (domaine d'entreprise)</li>
                <li>Les emails personnels (Gmail, Yahoo, Hotmail, etc.) sont bloqués pour des raisons de conformité</li>
                <li>Vous devez avoir l'autorité nécessaire pour engager votre organisation</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl text-white font-heading mb-3">4. Plans et Paiements</h2>
              <p className="mb-2">Le Service propose trois plans tarifaires :</p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li><strong>Free :</strong> 1 audit gratuit par organisation (IP/domaine email)</li>
                <li><strong>Auditor :</strong> 99€/mois — 10 audits par mois</li>
                <li><strong>Firm :</strong> 249€/mois — Audits illimités</li>
              </ul>
              <p className="mt-3">
                Les paiements sont traités via Stripe. Les abonnements sont mensuels et renouvelables automatiquement.
                Aucun remboursement n'est prévu pour les audits non utilisés.
              </p>
            </section>

            <section>
              <h2 className="text-xl text-white font-heading mb-3">5. Utilisation Acceptable</h2>
              <p className="mb-2">Vous vous engagez à :</p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li>Utiliser le Service uniquement à des fins légales et professionnelles</li>
                <li>Ne pas tenter de contourner les limites techniques ou les restrictions d'accès</li>
                <li>Ne pas transmettre de fichiers contenant des malwares, virus ou code malveillant</li>
                <li>Respecter les droits de propriété intellectuelle de SheetArmor</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl text-white font-heading mb-3">6. Propriété Intellectuelle</h2>
              <p>
                Vous conservez tous les droits sur vos fichiers Excel et les données soumises. SheetArmor conserve tous les droits sur le logiciel,
                les rapports générés, et la technologie d'analyse. Les rapports PDF sont fournis sous licence d'utilisation non exclusive.
              </p>
            </section>

            <section>
              <h2 className="text-xl text-white font-heading mb-3">7. Limitation de Responsabilité</h2>
              <p>
                SheetArmor est fourni "en l'état" sans garantie d'exactitude absolue. Les rapports d'audit sont des outils d'assistance à la décision
                et ne remplacent pas un audit humain professionnel. SheetArmor ne peut être tenu responsable des décisions prises sur la base des rapports générés.
                Notre responsabilité est limitée au montant des frais payés au cours des 12 derniers mois.
              </p>
            </section>

            <section>
              <h2 className="text-xl text-white font-heading mb-3">8. Résiliation</h2>
              <p>
                Vous pouvez résilier votre compte à tout moment depuis les paramètres. SheetArmor se réserve le droit de suspendre ou résilier
                votre accès en cas de violation des présentes conditions, sans préavis ni remboursement.
              </p>
            </section>

            <section>
              <h2 className="text-xl text-white font-heading mb-3">9. Modifications</h2>
              <p>
                SheetArmor se réserve le droit de modifier ces conditions à tout moment. Les utilisateurs seront informés par email des modifications importantes.
                La poursuite de l'utilisation du Service après modification vaut acceptation des nouvelles conditions.
              </p>
            </section>

            <section>
              <h2 className="text-xl text-white font-heading mb-3">10. Droit Applicable</h2>
              <p>
                Ces conditions sont régies par le droit français. Tout litige relève de la compétence exclusive des tribunaux de Paris, France.
              </p>
            </section>

            <section>
              <h2 className="text-xl text-white font-heading mb-3">11. Contact</h2>
              <p>
                Pour toute question concernant ces conditions, contactez-nous à : <a href="mailto:hello@sheetarmor.io" className="text-blue-500 hover:text-blue-400">hello@sheetarmor.io</a>
              </p>
            </section>
          </div>
        ) : (
          <div className="space-y-8 text-neutral-300 font-body leading-relaxed">
            <section>
              <h2 className="text-xl text-white font-heading mb-3">1. Acceptance of Terms</h2>
              <p>
                By accessing and using SheetArmor (the "Service"), you agree to be bound by these Terms of Service.
                If you do not accept these terms, please do not use the Service.
              </p>
            </section>

            <section>
              <h2 className="text-xl text-white font-heading mb-3">2. Service Description</h2>
              <p>
                SheetArmor is a B2B SaaS platform providing automated auditing of Excel files (.xlsx) through deterministic analysis
                combined with an artificial intelligence engine. The Service generates professional PDF reports identifying potential risks
                in financial models.
              </p>
            </section>

            <section>
              <h2 className="text-xl text-white font-heading mb-3">3. Access Requirements</h2>
              <p className="mb-2">
                The Service is exclusively reserved for businesses and professional organizations. Registration requires:
              </p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li>A corporate email address (company domain)</li>
                <li>Personal emails (Gmail, Yahoo, Hotmail, etc.) are blocked for compliance reasons</li>
                <li>You must have the necessary authority to bind your organization</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl text-white font-heading mb-3">4. Plans and Payments</h2>
              <p className="mb-2">The Service offers three pricing plans:</p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li><strong>Free:</strong> 1 free audit per organization (IP/email domain)</li>
                <li><strong>Auditor:</strong> $99/month — 10 audits per month</li>
                <li><strong>Firm:</strong> $249/month — Unlimited audits</li>
              </ul>
              <p className="mt-3">
                Payments are processed via Stripe. Subscriptions are monthly and automatically renewable.
                No refunds are provided for unused audits.
              </p>
            </section>

            <section>
              <h2 className="text-xl text-white font-heading mb-3">5. Acceptable Use</h2>
              <p className="mb-2">You agree to:</p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li>Use the Service only for legal and professional purposes</li>
                <li>Not attempt to bypass technical limits or access restrictions</li>
                <li>Not transmit files containing malware, viruses, or malicious code</li>
                <li>Respect SheetArmor's intellectual property rights</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl text-white font-heading mb-3">6. Intellectual Property</h2>
              <p>
                You retain all rights to your Excel files and submitted data. SheetArmor retains all rights to the software,
                generated reports, and analysis technology. PDF reports are provided under a non-exclusive usage license.
              </p>
            </section>

            <section>
              <h2 className="text-xl text-white font-heading mb-3">7. Limitation of Liability</h2>
              <p>
                SheetArmor is provided "as is" without warranty of absolute accuracy. Audit reports are decision support tools
                and do not replace professional human auditing. SheetArmor cannot be held liable for decisions made based on generated reports.
                Our liability is limited to the amount of fees paid in the last 12 months.
              </p>
            </section>

            <section>
              <h2 className="text-xl text-white font-heading mb-3">8. Termination</h2>
              <p>
                You may terminate your account at any time from settings. SheetArmor reserves the right to suspend or terminate
                your access for violation of these terms, without notice or refund.
              </p>
            </section>

            <section>
              <h2 className="text-xl text-white font-heading mb-3">9. Modifications</h2>
              <p>
                SheetArmor reserves the right to modify these terms at any time. Users will be notified by email of significant changes.
                Continued use of the Service after modifications constitutes acceptance of the new terms.
              </p>
            </section>

            <section>
              <h2 className="text-xl text-white font-heading mb-3">10. Governing Law</h2>
              <p>
                These terms are governed by French law. Any disputes fall under the exclusive jurisdiction of the courts of Paris, France.
              </p>
            </section>

            <section>
              <h2 className="text-xl text-white font-heading mb-3">11. Contact</h2>
              <p>
                For any questions regarding these terms, contact us at: <a href="mailto:hello@sheetarmor.io" className="text-blue-500 hover:text-blue-400">hello@sheetarmor.io</a>
              </p>
            </section>
          </div>
        )}
      </div>
    </div>
  );
}
